import React, { useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { ShieldCheck, Globe, Receipt, ChartLineUp } from "@phosphor-icons/react";
import { Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Lockup, LoginLockup } from "@/components/Logo";
import { DecisionDialog } from "@/components/DecisionDialog";

// ── The "Explore the demo" credentials ──────────────────────────────────────
//
// WHY THESE COME FROM THE BUILD AND NOT FROM THIS FILE. They used to be written
// here as a literal address and password. That account was seeded on the old
// hosting platform and never recreated after the move, so from the day
// production moved, every prospect who clicked the button got "invalid email or
// password" — a dead button as the first thing anyone saw. (ZEN-363.)
//
// Two things follow from a demo account that ANY MEMBER OF THE PUBLIC signs
// into, and they pull in opposite directions:
//
//   * The password is NOT a secret and cannot be made one. The browser has to
//     possess it in order to send it, so it is readable by anyone who looks at
//     this bundle. Nothing here pretends otherwise.
//   * It is still kept OUT OF THE REPOSITORY, because that buys two real
//     things: it can be rotated without a code change and a redeploy of the
//     source, and the production credential never appears in anything that
//     greps this repository.
//
// NO FALLBACK VALUE, deliberately. If a build has not been given these, the
// button DOES NOT RENDER — strictly better than a button that fails, and
// exactly the alternative the ticket named to seeding.
// `backend/tests/test_demo_credentials_static.py` fails the build if a literal
// or a fallback is ever put back.
const DEMO_EMAIL = process.env.REACT_APP_DEMO_EMAIL;
const DEMO_PASSWORD = process.env.REACT_APP_DEMO_PASSWORD;
const DEMO_AVAILABLE = Boolean(DEMO_EMAIL && DEMO_PASSWORD);

export default function AuthPage() {
  const { login, register, confirmSignup, adoptSession, sessionSnapshot } = useAuth();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("reset_token");
  // The link we email a new customer lands here (ZEN-226), read exactly the way
  // the password-reset link already is.
  const signupToken = searchParams.get("signup_token");

  // The "you already have an account" email carries two links, and they must go
  // to two different places. They used to be the same address, so "Reset it
  // here" opened the sign-in form — the one screen that cannot help somebody who
  // has forgotten their password.
  const wantsForgot = searchParams.get("forgot") === "1";

  const [mode, setMode] = useState(
    signupToken ? "confirm" : resetToken ? "reset" : wantsForgot ? "forgot" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  // The server's own sentence, held rather than hard-coded here, so the screen
  // and the API can never drift into saying two different things.
  const [signupSent, setSignupSent] = useState("");
  const [demoWarning, setDemoWarning] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (mode === "login") {
        await login(email, password);
      } else if (mode === "register") {
        // Only the address here. The name and the password are both chosen on
        // the screen the emailed link lands on — see confirmSignup in
        // AuthContext for why nothing a stranger types may reach the account.
        setSignupSent(await register(email));
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setForgotSent(true);
      } else if (mode === "confirm") {
        if (password !== confirmPassword) {
          setErr("Passwords do not match");
          setBusy(false);
          return;
        }
        // Courtesy check only — the server is the gate (ZEN-152). Kept in step
        // with the server's minimum so somebody is warned before they submit.
        if (password.length < 8) {
          setErr("Password must be at least 8 characters");
          setBusy(false);
          return;
        }
        if (!name.trim()) {
          setErr("Enter the name to put on this account");
          setBusy(false);
          return;
        }
        if (!(await confirmSignup(signupToken, name, password))) {
          setErr("We could not sign you in. Please sign in below.");
        }
      } else if (mode === "reset") {
        if (password !== confirmPassword) {
          setErr("Passwords do not match");
          setBusy(false);
          return;
        }
        if (password.length < 8) {
          setErr("Password must be at least 8 characters");
          setBusy(false);
          return;
        }
        // WHO WAS SIGNED IN BEFORE THIS REQUEST WENT OUT (ZEN-432/433).
        //
        // This is the one thing only THIS side can know. A reset takes a round
        // trip, and another tab can sign in during it — after which installing
        // this reset would put one account on screen while requests go out as
        // another. AuthContext cannot work that out for itself afterwards: by
        // then it may have already adopted the newer session, and the two look
        // identical to any check made after the fact.
        //
        // So the snapshot is taken here, before the request, and handed back
        // with the answer. Found by adversarial review, round 6.
        const before = sessionSnapshot();
        const { data } = await api.post("/auth/reset-password", { token: resetToken, password });
        // Straight in, without going back through a door somebody may be
        // holding shut (ZEN-145). If the server sent no session, fall through
        // to the "now sign in" screen exactly as before.
        if (await adoptSession(data, before)) return;
        setResetDone(true);
      }
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setBusy(false);
    }
  };

  // NOTHING HAPPENS ON ARRIVAL ANY MORE. The link used to be redeemed the moment
  // this page opened; the password is now chosen here, so the page shows a form
  // and waits. That also removes a development-only hazard review noticed: React
  // runs mount effects twice in the development build, and an effect that fired
  // a request could send it twice — the first creating the account and the second
  // showing a refusal for it.

  const goToLogin = () => {
    setMode("login");
    setErr("");
    setForgotSent(false);
    setResetDone(false);
    setSignupSent("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setPassword("");
    setConfirmPassword("");
  };

  // Re-entrancy latch for the demo sign-in. `busy` disables the buttons, but a
  // state flag only takes effect on the NEXT render — and the dialog's confirm
  // button stays clickable through its closing animation. Two quick clicks
  // would fire two sign-ins at the ONE shared, rate-limited demo account,
  // burning the whole public's attempt budget. A ref is checked and set in the
  // same tick, so the second click is a no-op.
  const demoInFlight = useRef(false);

  const fillDemo = async () => {
    if (demoInFlight.current) return;
    demoInFlight.current = true;
    setBusy(true);
    setErr("");
    setMode("login");
    try {
      await login(DEMO_EMAIL, DEMO_PASSWORD);
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      demoInFlight.current = false;
      setBusy(false);
    }
  };

  // The demo must never silently replace a real session (ZEN-413). fillDemo is
  // the ordinary sign-in path, and signing in REPLACES whoever is already
  // signed in on this browser. A signed-in person can reach this button: this
  // page renders while the session is still being checked, so the button is
  // live before the redirect into the app fires — and from that seat one click
  // swapped a real family for the demo family with nothing on screen but the
  // data changing, which is indistinguishable from a data breach.
  //
  // The STORED TOKEN is the fact consulted, not the loaded user: during
  // exactly the window where the accident happens the user object is still
  // null, but the token is already there.
  const demoClick = () => {
    if (localStorage.getItem("token")) {
      setDemoWarning(true);
      return;
    }
    fillDemo();
  };

  /* ---- form title / subtitle ---- */
  const titles = {
    login: ["Welcome back", "Sign in to manage your 529 expenses."],
    register: ["Create your account", "Tell us your email address and we will send you a link to finish."],
    forgot: ["Reset your password", "Enter the email address linked to your account."],
    reset: ["Set a new password", "Choose a strong password for your account."],
    // Two headings for one mode: while the link is being redeemed, and after it
    // has failed. Leaving "Setting up your account" over a message saying the
    // link is dead reads as though something is still happening.
    confirm: ["Finish setting up your account", "Last step — your name, a password, and you are in."],
  };
  const [title, subtitle] = titles[mode] || titles.login;

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between bg-[#034AA2] p-12 text-white lg:flex">
        <div>
          <LoginLockup height={80} />
        </div>
        <div>
          <h1 className="max-w-xl text-4xl font-extrabold leading-tight tracking-tight xl:text-5xl">
            Every dollar traced.<br />Every year projected.
          </h1>
          <p className="mt-5 max-w-md text-base text-blue-100">
            Track what you&apos;ve already spent, see what&apos;s still ahead, and keep every dollar reconciled by tax year — from K-12 through trade school, college, and university.
          </p>
          <div className="mt-8 space-y-3">
            {[
              [Receipt, "Snap or upload a receipt — AI fills in the details"],
              [ChartLineUp, "Spot a shortfall — or a surplus — years ahead"],
              [Globe, "Multi-currency with date-correct exchange rates"],
              [ShieldCheck, "Tax-year reconciliation and audit-ready exports"],
            ].map(([Ic, t]) => (
              <div key={t} className="flex items-center gap-3 text-sm text-blue-50">
                <Ic size={20} weight="bold" className="shrink-0 text-[#F5D85B]" /> {t}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-blue-200">Record-keeping tool — not tax, legal, or financial advice.</p>
      </div>

      {/* Form */}
      <div className="flex flex-1 flex-col items-center justify-center bg-[#E9E9EB] p-6">
        {/* Mobile brand block — above the card, hidden on desktop */}
        <div className="mb-4 text-center lg:hidden" data-testid="mobile-brand-block">
          <div className="inline-flex items-center justify-center">
            <Lockup height={48} />
          </div>
          <p className="mt-2 text-xl font-extrabold tracking-tight text-[#034AA2]" data-testid="mobile-tagline">
            Every dollar traced. Every year projected.
          </p>
        </div>

        <div className="w-full max-w-md rounded-2xl border border-[#C2C9D1] bg-white p-8 shadow-sm fade-in">

          {/* Back arrow for forgot / reset modes */}
          {(mode === "forgot" || mode === "reset") && !forgotSent && !resetDone && (
            <button
              onClick={goToLogin}
              data-testid="auth-back-to-login"
              className="mb-4 flex items-center gap-1 text-sm font-medium text-[#6b7280] transition-colors hover:text-[#034AA2]"
            >
              <ArrowLeft size={16} /> Back to sign in
            </button>
          )}

          <h2 className="text-2xl font-extrabold tracking-tight" data-testid="auth-title">{title}</h2>
          <p className="mt-1 text-sm text-[#6b7280]">{subtitle}</p>

          {/* ---- CONFIRM: the emailed link, and the password chosen here ---- */}
          {mode === "confirm" ? (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-confirm">
              <Field
                label="Full name"
                value={name}
                onChange={setName}
                testid="auth-name"
                placeholder="Jordan Parent"
                autoComplete="name"
              />
              <PasswordField
                label="Choose a password"
                value={password}
                onChange={setPassword}
                show={showPassword}
                onToggle={() => setShowPassword((v) => !v)}
                testid="auth-password"
                placeholder="Create a password"
                autoComplete="new-password"
              />
              <p className="-mt-2 text-xs text-[#6b7280]" data-testid="auth-password-hint">At least 8 characters</p>
              <PasswordField
                label="Confirm password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                show={showConfirmPassword}
                onToggle={() => setShowConfirmPassword((v) => !v)}
                testid="auth-confirm-password"
                placeholder="Re-enter your password"
                autoComplete="new-password"
              />
              {err && (
                <div data-testid="signup-confirm-error" className="rounded-md bg-[#F01919]/10 px-3 py-2 text-sm font-medium text-[#F01919]">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="auth-submit"
                className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#034AA2] font-semibold text-white transition-colors hover:bg-[#023a80] disabled:opacity-60"
              >
                {busy && <Loader2 size={18} className="animate-spin" />}
                {busy ? "Setting up your account…" : "Create my account"}
              </button>
              {err && (
                <button
                  type="button"
                  onClick={() => { setMode("register"); setErr(""); setPassword(""); setConfirmPassword(""); }}
                  data-testid="signup-confirm-retry"
                  className="h-11 w-full rounded-lg border border-[#C2C9D1] bg-transparent font-semibold text-[#1f2937] transition-colors hover:bg-[#E9E9EB]"
                >
                  Sign up again
                </button>
              )}
            </form>
          ) : mode === "register" && signupSent ? (
            /* ---- REGISTER: check your email ---- */
            <div className="mt-6 space-y-4" data-testid="signup-sent">
              <div className="rounded-lg bg-[#077A39]/10 px-4 py-3 text-sm text-[#077A39]">
                {signupSent}
              </div>
              <p className="text-sm text-[#6b7280]" data-testid="signup-sent-ttl">
                The link works for 24 hours.
              </p>
              <button
                onClick={goToLogin}
                data-testid="signup-sent-signin"
                className="h-11 w-full rounded-lg bg-[#034AA2] font-semibold text-white transition-colors hover:bg-[#023a80]"
              >
                Back to sign in
              </button>
            </div>
          ) : /* ---- FORGOT: success state ---- */
          mode === "forgot" && forgotSent ? (
            <div className="mt-6 space-y-4" data-testid="forgot-success">
              <div className="rounded-lg bg-[#077A39]/10 px-4 py-3 text-sm text-[#077A39]">
                {/* The attempt, not the arrival, in the same words for every address (ZEN-301; wording approved by Maryse, 30 Oct 2026). */}
                If that email address has an account with us, we are sending a password reset link to it now. Look in your inbox, and in your spam folder, over the next few minutes.
              </div>
              <button
                onClick={goToLogin}
                data-testid="auth-back-to-signin"
                className="h-11 w-full rounded-lg bg-[#034AA2] font-semibold text-white transition-colors hover:bg-[#023a80]"
              >
                Back to sign in
              </button>
            </div>
          ) : mode === "reset" && resetDone ? (
            /* ---- RESET: success state ---- */
            <div className="mt-6 space-y-4" data-testid="reset-success">
              <div className="rounded-lg bg-[#077A39]/10 px-4 py-3 text-sm text-[#077A39]">
                Your password has been updated. You can now sign in with your new password.
              </div>
              <button
                onClick={goToLogin}
                data-testid="auth-back-to-signin-after-reset"
                className="h-11 w-full rounded-lg bg-[#034AA2] font-semibold text-white transition-colors hover:bg-[#023a80]"
              >
                Sign in
              </button>
            </div>
          ) : (
            /* ---- Active form ---- */
            <form onSubmit={submit} className="mt-6 space-y-4">

              {mode !== "reset" && (
                <Field
                  label="Email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  testid="auth-email"
                  placeholder="you@email.com"
                  autoComplete="email"
                />
              )}
              {mode !== "forgot" && mode !== "register" && (
                <PasswordField
                  label={mode === "reset" ? "New password" : "Password"}
                  value={password}
                  onChange={setPassword}
                  show={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                  testid="auth-password"
                  placeholder={mode === "login" ? "Enter your password" : mode === "reset" ? "Enter new password" : "Create a password"}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                />
              )}
              {mode === "reset" && (
                <p className="-mt-2 text-xs text-[#6b7280]" data-testid="auth-password-hint">At least 8 characters</p>
              )}

              {/* Forgot password link — directly beneath password, login mode only */}
              {mode === "login" && (
                <div className="-mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => { setMode("forgot"); setErr(""); setShowPassword(false); }}
                    data-testid="auth-forgot-password"
                    className="text-sm font-medium text-[#034AA2] transition-colors hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {mode === "reset" && (
                <PasswordField
                  label="Confirm password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  show={showConfirmPassword}
                  onToggle={() => setShowConfirmPassword((v) => !v)}
                  testid="auth-confirm-password"
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                />
              )}

              {mode === "reset" && (
                <p className="text-sm font-medium text-[#374151]" data-testid="auth-reset-signs-out">
                  Saving a new password signs out every other device that is signed in to your account.
                </p>
              )}

              {err && (
                <div data-testid="auth-error" className="rounded-md bg-[#F01919]/10 px-3 py-2 text-sm font-medium text-[#F01919]">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="auth-submit"
                className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#034AA2] font-semibold text-white transition-colors hover:bg-[#023a80] disabled:opacity-60"
              >
                {busy && <Loader2 size={18} className="animate-spin" />}
                {busy
                  ? "Please wait..."
                  : mode === "login"
                    ? "Sign in"
                    : mode === "register"
                      ? "Create account"
                      : mode === "forgot"
                        ? "Send reset link"
                        : "Update password"}
              </button>
            </form>
          )}

          {/* Demo button + mode toggle */}
          {(mode === "login" || (mode === "register" && !signupSent)) && (
            <div className="mt-4 space-y-3">
              {/* Only offered when this build was actually given demo
                  credentials. A button that cannot work is worse than no
                  button — that was the whole of ZEN-363. */}
              {DEMO_AVAILABLE && (
                <button
                  type="button"
                  onClick={demoClick}
                  disabled={busy}
                  data-testid="auth-demo"
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-[#C2C9D1] bg-transparent font-semibold text-[#1f2937] transition-colors hover:bg-[#E9E9EB] disabled:opacity-60"
                >
                  {busy && <Loader2 size={18} className="animate-spin" />}
                  Explore the demo
                </button>
              )}
              <div className="text-center">
                <button
                  onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); setShowPassword(false); }}
                  className="text-sm font-semibold text-[#034AA2] hover:underline"
                  data-testid="auth-toggle"
                >
                  {mode === "login" ? "Need an account? Sign up" : "Have an account? Sign in"}
                </button>
              </div>
            </div>
          )}

          {/* Mobile-only disclaimer */}
          <div className="mt-4 border-t border-[#E9E9EB] pt-3 lg:hidden">
            <p className="text-center text-xs text-[#6b7280]">Record-keeping tool — not tax, legal, or financial advice.</p>
          </div>
        </div>
      </div>

      {/* Asked, never ejected (ZEN-413): a signed-in person must be able to
          decline. Declining does nothing at all — their session is untouched. */}
      <DecisionDialog
        open={demoWarning}
        onClose={() => setDemoWarning(false)}
        onConfirm={() => { setDemoWarning(false); fillDemo(); }}
        title="You're signed in — open the demo?"
        actionLabel="Sign out and open the demo"
        busy={busy}
        testid="demo-warning"
      >
        <p className="text-sm text-[#6b7280]" data-testid="demo-warning-body">
          The demo is a separate example family. Opening it will sign you out
          of your own account on this browser. Your records are not changed in
          any way, and you can sign back in at any time.
        </p>
      </DecisionDialog>
    </div>
  );
}

/* ---- Simple text field ---- */
function Field({ label, value, onChange, type = "text", testid, placeholder, autoComplete }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-[#1f2937]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required
        className="h-11 w-full rounded-lg border border-[#C2C9D1] bg-white px-3 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30"
      />
    </label>
  );
}

/* ---- Password field with eye toggle ---- */
function PasswordField({ label, value, onChange, show, onToggle, testid, placeholder, autoComplete }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-[#1f2937]">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          className="h-11 w-full rounded-lg border border-[#C2C9D1] bg-white px-3 pr-11 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30"
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testid}-toggle`}
          className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-[#6b7280] transition-colors hover:text-[#1f2937]"
          tabIndex={-1}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </label>
  );
}
